import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { profileOptionsQuery } from "@amber/collection/ui";
import {
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  RadioCards,
  ReasonField,
  Select,
  TextField,
  TextInput,
  errorText,
  isApiError,
  useCsrf,
  useReason,
  useToast,
} from "@amber/ui";
import {
  CONTEXT_OPTIONS,
  CONTEXT_OPTION_LABELS,
  DATE_BASIS_LABELS,
  DATE_BASIS_OPTIONS,
  recordDetailsResponseSchema,
  relationChangeResponseSchema,
  splitResponseSchema,
  type CommunicationView,
  type EvidenceRecord,
  type RecordDetailsPreview,
  type TurnView,
} from "../../contracts/index";
import { OpenReports, type Editing } from "../editing/Editing";
import { EVIDENCE_ERROR_FALLBACK, REQUIRED_REASON, clock } from "../format";
import { detailsPreviewQuery, mergePreviewQuery, splitPreviewQuery, useEvidenceCommand } from "../queries";

const CONTEXT_FROM_STATUS: Record<string, (typeof CONTEXT_OPTIONS)[number]> = {
  complete: "complete",
  partial: "partial",
  unavailable_original: "missing",
  unresolved: "unknown",
};

function useLease(editing: Editing, communicationId: string) {
  const [lease, setLease] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    editing.acquire(communicationId).then(
      (l) => {
        if (live) setLease(l.lease_id);
      },
      (e: unknown) => {
        if (live) setError(errorText(e, EVIDENCE_ERROR_FALLBACK));
      },
    );
    return () => {
      live = false;
      void editing.release();
    };
  }, [editing, communicationId]);
  return { lease, error };
}

type SpeakerOption = EvidenceRecord["speakers"][number];
type ProfileOption = { id: string; name: string; lifecycle_state: string };

function ProfileLinks({
  named,
  profiles,
  links,
  setLinks,
  focusSpeaker,
}: {
  named: readonly SpeakerOption[];
  profiles: readonly ProfileOption[];
  links: Record<string, string>;
  setLinks: (links: Record<string, string>) => void;
  focusSpeaker: string | null;
}) {
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend className="mb-1 text-sm font-strong tracking-[0.04em] text-muted uppercase">Speaker profile links</legend>
      {named.length === 0 ? <p className="m-0 text-md text-muted">No named speakers in this record.</p> : null}
      {named.map((s) => (
        <div key={s.id} className="flex items-center gap-2 py-1">
          <label htmlFor={`link-${s.id}`} className="w-[180px] truncate text-md">
            {s.display}
          </label>
          <Select
            id={`link-${s.id}`}
            autoFocus={s.id === focusSpeaker}
            className="w-[260px]"
            value={links[s.id] ?? ""}
            onChange={(e) => {
              setLinks({ ...links, [s.id]: e.target.value });
            }}
          >
            <option value="">{s.profile ? `Keep: ${s.profile.name}` : "Keep: no profile"}</option>
            {profiles
              .filter((p) => p.id !== s.profile?.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {s.profile_match.candidates.some((c) => c.id === p.id) ? " · candidate" : ""} · {p.lifecycle_state}
                </option>
              ))}
            {s.profile ? <option value="none">No profile</option> : null}
          </Select>
        </div>
      ))}
    </fieldset>
  );
}

function OccurrenceFields({
  date,
  setDate,
  basis,
  setBasis,
  published,
}: {
  date: string;
  setDate: (date: string) => void;
  basis: (typeof DATE_BASIS_OPTIONS)[number];
  setBasis: (basis: (typeof DATE_BASIS_OPTIONS)[number]) => void;
  published: string | null;
}) {
  return (
    <div className="mt-3 grid grid-cols-[160px_1fr] gap-x-3 max-[640px]:grid-cols-1">
      <div>
        <FieldLabel htmlFor="occ-date">Occurrence date</FieldLabel>
        <TextInput
          id="occ-date"
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
      </div>
      <div>
        <FieldLabel htmlFor="occ-basis">Basis</FieldLabel>
        <Select
          id="occ-basis"
          value={basis}
          onChange={(e) => {
            setBasis(e.target.value as (typeof DATE_BASIS_OPTIONS)[number]);
          }}
        >
          {DATE_BASIS_OPTIONS.map((b) => (
            <option key={b} value={b}>
              {DATE_BASIS_LABELS[b]}
            </option>
          ))}
        </Select>
        <p className="m-0 mt-1 text-md text-muted">
          Never after publication
          {published ? ` (${published.slice(0, 10)})` : ""}.
        </p>
      </div>
    </div>
  );
}

function DetailsImpact({ impact }: { impact: RecordDetailsPreview }) {
  return (
    <>
      <table className="w-full border-collapse text-md">
        <caption className="sr-only">Changes</caption>
        <thead>
          <tr className="text-left text-sm text-muted">
            <th className="py-1 font-strong">Field</th>
            <th className="py-1 font-strong">Before</th>
            <th className="py-1 font-strong">After</th>
          </tr>
        </thead>
        <tbody>
          {impact.changes.map((c) => (
            <tr key={c.field} className="border-t border-divider">
              <td className="py-1.5 pr-2">{c.field}</td>
              <td className="py-1.5 pr-2 text-muted">{c.before}</td>
              <td className="py-1.5 font-strong">{c.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="m-0 mt-2 list-disc pl-5 text-md text-muted">
        <li>
          {impact.affected_turns} turn{impact.affected_turns === 1 ? "" : "s"} affected
        </li>
        <li>
          {impact.checks_to_rerun === 0
            ? "No passage checks to rerun"
            : `${String(impact.checks_to_rerun)} passage checks rerun`}
        </li>
        <li>{impact.consumers}</li>
        {impact.readiness.map((r) => (
          <li key={r.name ?? "none"}>
            {r.name ?? "No tracked person"}: {r.before === r.after ? `stays ${r.after}` : `${r.before} → ${r.after}`}
          </li>
        ))}
      </ul>
    </>
  );
}

function SaveFailure({ error }: { error: unknown }) {
  const conflict = isApiError(error) && error.code === "conflict";
  if (conflict)
    return (
      <Callout tone="attention" role="alert" className="mt-3">
        {errorText(error, EVIDENCE_ERROR_FALLBACK)} Go back to keep your draft, or close and reopen to start from the
        latest version.
      </Callout>
    );
  return <FailedCallout error={error} fallback={EVIDENCE_ERROR_FALLBACK} />;
}

function useDetailsDraft(communication: CommunicationView) {
  const initialDate = communication.occurrence.date?.slice(0, 10) ?? "";
  const initialContext = CONTEXT_FROM_STATUS[communication.context.status] ?? "unknown";
  const [links, setLinks] = useState<Record<string, string>>({});
  const [date, setDate] = useState(initialDate);
  const [basis, setBasis] = useState<(typeof DATE_BASIS_OPTIONS)[number]>("broadcast");
  const [context, setContext] = useState<(typeof CONTEXT_OPTIONS)[number]>(initialContext);
  const [missing, setMissing] = useState("");
  const [evidence, setEvidence] = useState("");
  const profileLinks = Object.entries(links).flatMap(([speaker_id, v]) =>
    v === "" ? [] : [{ speaker_id, profile_id: v === "none" ? null : v }],
  );
  const dateChanged = date !== "" && date !== initialDate;
  const contextChanged = context !== initialContext || missing.trim() !== "";
  return {
    links,
    setLinks,
    date,
    setDate,
    basis,
    setBasis,
    context,
    setContext,
    missing,
    setMissing,
    evidence,
    setEvidence,
    profileLinks,
    occurrence: dateChanged ? { date, basis } : null,
    contextChange: contextChanged ? { value: context, missing: missing.trim() } : null,
    evidenceError: evidence.trim() ? null : "Supporting evidence is required: a timestamp, paragraph, note or source",
  };
}

type DetailsDraft = ReturnType<typeof useDetailsDraft>;

function DetailsForm({
  communication,
  named,
  profiles,
  published,
  draft,
  evidenceError,
  focusSpeaker,
}: {
  communication: CommunicationView;
  named: readonly SpeakerOption[];
  profiles: readonly ProfileOption[];
  published: string | null;
  draft: DetailsDraft;
  evidenceError: string | null;
  focusSpeaker: string | null;
}) {
  return (
    <>
      <DialogText>
        {communication.title ?? `Communication ${String(communication.ordinal)}`} · the archived source stays unchanged
      </DialogText>
      <ProfileLinks
        named={named}
        profiles={profiles}
        links={draft.links}
        setLinks={draft.setLinks}
        focusSpeaker={focusSpeaker}
      />
      <OccurrenceFields
        date={draft.date}
        setDate={draft.setDate}
        basis={draft.basis}
        setBasis={draft.setBasis}
        published={published}
      />
      <RadioCards
        label="Context"
        value={draft.context}
        onChange={draft.setContext}
        options={CONTEXT_OPTIONS.map((c) => ({ value: c, label: CONTEXT_OPTION_LABELS[c] }))}
      />
      <TextField
        label="What is missing"
        optional="optional"
        value={draft.missing}
        onChange={(e) => {
          draft.setMissing(e.target.value);
        }}
        placeholder="e.g. the opening minutes"
      />
      <TextField
        label="Supporting evidence"
        required
        value={draft.evidence}
        onChange={(e) => {
          draft.setEvidence(e.target.value);
        }}
        placeholder="Timestamp or paragraph in this item, or a note or source"
        error={evidenceError}
      />
    </>
  );
}

export function RecordDetailsDialog({
  record,
  communication,
  focusSpeaker,
  editing,
  onClose,
}: {
  record: EvidenceRecord;
  communication: CommunicationView;
  focusSpeaker: string | null;
  editing: Editing;
  onClose: () => void;
}) {
  const toast = useToast();
  const csrf = useCsrf();
  const { lease, error: leaseError } = useLease(editing, communication.id);
  const [base] = useState(communication.version?.revision_id ?? "");
  const [step, setStep] = useState<1 | 2>(1);
  const named = record.speakers.filter((s) => s.name);
  const profiles = useQuery(profileOptionsQuery);
  const draft = useDetailsDraft(communication);
  const reason = useReason(null, REQUIRED_REASON);
  const [resolves, setResolves] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const open = record.problems.filter((p) => p.state === "open" && p.communication === communication.ordinal);
  const body = {
    lease_id: lease ?? "lse_00000000000000000000000000",
    base_revision_id: base,
    reason: reason.trimmed || "-",
    evidence: draft.evidence.trim() || "-",
    profile_links: draft.profileLinks,
    occurrence: draft.occurrence,
    context: draft.contextChange,
    resolves,
  };
  const preview = useQuery(detailsPreviewQuery(communication.id, body, step === 2 && lease !== null, csrf));
  const save = useEvidenceCommand(record.item.id, recordDetailsResponseSchema);
  const evidenceError = draft.evidenceError;
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={step === 1 ? "Correct record details…" : "Review the correction"}
      onSubmit={() => {
        if (step === 1) {
          setSubmitted(true);
          if (!evidenceError) {
            setSubmitted(false);
            setStep(2);
          }
          return;
        }
        if (reason.submit() && lease)
          save.mutate(
            {
              path: `/api/evidence/communications/${communication.id}/details`,
              body: { ...body, reason: reason.trimmed, evidence: draft.evidence.trim() },
            },
            {
              onSuccess: () => {
                toast.show("Record details corrected; a new version was saved");
                void editing.release();
                onClose();
              },
            },
          );
      }}
      footer={
        step === 1 ? (
          <DialogFooter onClose={onClose} confirm="Preview impact" disabled={!lease} />
        ) : (
          <DialogFooter
            onClose={() => {
              setStep(1);
            }}
            cancel="Back"
            confirm="Save correction"
            disabled={!preview.data}
            pending={save.isPending}
          />
        )
      }
    >
      {leaseError ? (
        <Callout tone="attention" role="alert">
          {leaseError}
        </Callout>
      ) : null}
      {step === 1 ? (
        <DetailsForm
          communication={communication}
          named={named}
          profiles={profiles.data?.profiles ?? []}
          published={record.item.dates.published?.value ?? null}
          draft={draft}
          evidenceError={submitted ? evidenceError : null}
          focusSpeaker={focusSpeaker}
        />
      ) : (
        <>
          {preview.isLoading ? <DialogText>Preparing the impact preview…</DialogText> : null}
          {preview.error ? (
            <Callout tone="attention" role="alert">
              {errorText(preview.error, EVIDENCE_ERROR_FALLBACK)}
            </Callout>
          ) : null}
          {preview.data ? <DetailsImpact impact={preview.data} /> : null}
          {open.length > 0 ? <OpenReports problems={open} picked={resolves} onPick={setResolves} /> : null}
          <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
          <SaveFailure error={save.error} />
        </>
      )}
    </Dialog>
  );
}

export function SplitCommunicationDialog({
  record,
  turn,
  editing,
  onClose,
}: {
  record: EvidenceRecord;
  turn: TurnView;
  editing: Editing;
  onClose: () => void;
}) {
  const toast = useToast();
  const csrf = useCsrf();
  const communication = record.communications.find((c) => c.ordinal === turn.communication) ?? record.communications[0];
  const { lease, error: leaseError } = useLease(editing, communication?.id ?? "");
  const reason = useReason(null, REQUIRED_REASON);
  const body = {
    lease_id: lease ?? "lse_00000000000000000000000000",
    base_revision_id: communication?.version?.revision_id ?? "",
    at_unit_id: turn.unit_id ?? "",
    reason: reason.trimmed || "-",
  };
  const preview = useQuery(
    splitPreviewQuery(communication?.id ?? "", turn.unit_id, body, Boolean(communication && turn.unit_id), csrf),
  );
  const save = useEvidenceCommand(record.item.id, splitResponseSchema);
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Split communication…"
      onSubmit={() => {
        if (reason.submit() && lease)
          save.mutate(
            {
              path: `/api/evidence/communications/${communication?.id ?? ""}/split`,
              body: { ...body, reason: reason.trimmed },
            },
            {
              onSuccess: () => {
                toast.show("Communication split; each part has its own readiness");
                void editing.release();
                onClose();
              },
            },
          );
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Split" disabled={!preview.data || !lease} pending={save.isPending} />
      }
    >
      {leaseError ? (
        <Callout tone="attention" role="alert">
          {leaseError}
        </Callout>
      ) : null}
      <DialogText>
        The second part starts with turn {turn.n} at {clock(turn.start_ms)}. Each part gets its own range, readiness and
        copies; old links keep working.
      </DialogText>
      {preview.error ? (
        <Callout tone="attention" role="alert">
          {errorText(preview.error, EVIDENCE_ERROR_FALLBACK)}
        </Callout>
      ) : null}
      {preview.data ? (
        <>
          <ol className="m-0 list-decimal pl-5 text-md">
            {preview.data.parts.map((p, i) => (
              <li key={String(i)} className="py-1">
                <span className="font-strong">
                  {clock(p.range_ms[0])}–{clock(p.range_ms[1])}
                </span>{" "}
                · {p.turns} turns ·{" "}
                {p.readiness.result === "eligible"
                  ? "Eligible"
                  : `Draft${p.readiness.reason ? ` · ${p.readiness.reason}` : ""}`}
                <div className="text-muted">
                  {p.people
                    .map(
                      (x) =>
                        `${x.name ?? "No tracked person"}: ${x.result === "eligible" ? "Eligible" : `Draft · ${x.reason ?? ""}`}`,
                    )
                    .join(" · ")}
                </div>
              </li>
            ))}
          </ol>
          <p className="m-0 mt-1 text-md text-muted">{preview.data.copies}</p>
        </>
      ) : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}

export function MergeCommunicationDialog({
  record,
  communication,
  editing,
  onClose,
}: {
  record: EvidenceRecord;
  communication: CommunicationView;
  editing: Editing;
  onClose: () => void;
}) {
  const toast = useToast();
  const csrf = useCsrf();
  const { lease, error: leaseError } = useLease(editing, communication.id);
  const reason = useReason(null, REQUIRED_REASON);
  const body = {
    lease_id: lease ?? "lse_00000000000000000000000000",
    base_revision_id: communication.version?.revision_id ?? "",
    reason: reason.trimmed || "-",
  };
  const preview = useQuery(mergePreviewQuery(communication.id, body, Boolean(communication.version), csrf));
  const save = useEvidenceCommand(record.item.id, relationChangeResponseSchema);
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Merge with the next communication…"
      onSubmit={() => {
        if (reason.submit() && lease)
          save.mutate(
            {
              path: `/api/evidence/communications/${communication.id}/merge`,
              body: { ...body, reason: reason.trimmed },
            },
            {
              onSuccess: (out) => {
                toast.show(out.message);
                void editing.release();
                onClose();
              },
            },
          );
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Merge" disabled={!preview.data || !lease} pending={save.isPending} />
      }
    >
      {leaseError ? (
        <Callout tone="attention" role="alert">
          {leaseError}
        </Callout>
      ) : null}
      {preview.error ? (
        <Callout tone="attention" role="alert">
          {errorText(preview.error, EVIDENCE_ERROR_FALLBACK)}
        </Callout>
      ) : null}
      {preview.data ? (
        <>
          <ol className="m-0 list-decimal pl-5 text-md">
            {preview.data.parts.map((p) => (
              <li key={p.title} className="py-1">
                <span className="font-strong">{p.title}</span> · {clock(p.range_ms[0])}–{clock(p.range_ms[1])} ·{" "}
                {p.turns} turns
              </li>
            ))}
          </ol>
          <DialogText>
            Becomes <b>{preview.data.result.title}</b> · {clock(preview.data.result.range_ms[0])}–
            {clock(preview.data.result.range_ms[1])} · {preview.data.result.turns} turns
          </DialogText>
          <ul className="m-0 list-disc pl-5 text-md text-muted">
            {preview.data.effects.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      ) : (
        <DialogText>Preparing the preview…</DialogText>
      )}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}
